import { useEffect, useState } from "react";
import UserService from "../Services/UserService";
import AppPasswordService from "../Services/AppPasswodService";
import PubSub from "pubsub-js";
import { Link, useParams } from "react-router-dom";
import AdminProfile from "./AdminProfile";
import AdminUserSettings from "./AdminUserSettings";
import SequenceService from "../Services/SequenceService";

function UserDetail() {

    const { id } = useParams();
    const [user, setUser] = useState({});
    const [campaigns, setCampaigns] = useState([]);
    
    // App Password states
    const [appPasswordStatus, setAppPasswordStatus] = useState({
        hasAppPassword: false,
        account: null
    });
    const [appPasswordForm, setAppPasswordForm] = useState({
        appPassword: ''
    });
    const [appPasswordErrors, setAppPasswordErrors] = useState({
        appPassword: ''
    });
    const [appPasswordTouched, setAppPasswordTouched] = useState({
        appPassword: false
    });
    const [isAppPasswordLoading, setIsAppPasswordLoading] = useState(false);

    useEffect(() => {
        getUserDetail();
        getUserCampaigns();
        getUserAppPasswordStatus();
    }, []);

    async function getUserDetail() {
        let user = await UserService.getUserById(id);
        setUser(user);
    }

    async function updateUserRole() {
        await UserService.updateUserById(id, { role: user.role });
        PubSub.publish('show-basic-notification', {
            title: 'Success',
            message: 'Role updated'
        });
    }

    async function getUserCampaigns() {
        let sequences = await SequenceService.getAdminSequences();
        let campaignsForUser = sequences.filter(s => s.user && s.user._id === id);
        setCampaigns(campaignsForUser);
    }

    async function getUserAppPasswordStatus() {
        try {
            const response = await AppPasswordService.getUserAppPasswordStatus(id);
            if (response.success) {
                setAppPasswordStatus({
                    hasAppPassword: response.hasAppPassword,
                    account: response.data?.account || null
                });
            }
        } catch (error) {
            console.error('Error getting app password status:', error);
        }
    }

    const validateAppPassword = (password) => {
        const nonSpaceCharacters = password.replace(/\s/g, '');
        if (!nonSpaceCharacters) return 'App password is required';
        if (nonSpaceCharacters.length !== 16) return 'App password must be 16 characters';
        return '';
    };

    const handleAppPasswordChange = (e) => {
        const { value } = e.target;
        setAppPasswordForm({ appPassword: value });
        
        const error = validateAppPassword(value);
        setAppPasswordErrors({ appPassword: error });
    };

    const handleAppPasswordBlur = () => {
        setAppPasswordTouched({ appPassword: true });
    };

    const testAppPassword = async () => {
        if (!appPasswordForm.appPassword.trim()) {
            setAppPasswordTouched({ appPassword: true });
            return;
        }

        const error = validateAppPassword(appPasswordForm.appPassword);
        if (error) {
            setAppPasswordErrors({ appPassword: error });
            setAppPasswordTouched({ appPassword: true });
            return;
        }

        setIsAppPasswordLoading(true);
        try {
            const cleanAppPassword = appPasswordForm.appPassword.replace(/\s/g, '');
            const response = await AppPasswordService.testAppPassword({
                account: user.email,
                password: cleanAppPassword
            });

            if (response.success) {
                PubSub.publish('show-basic-notification', {
                    title: 'Success',
                    message: 'App password is valid and connection successful!'
                });
            } else {
                PubSub.publish('show-basic-notification', {
                    title: 'Error',
                    message: response.message || 'App password test failed'
                });
            }
        } catch (error) {
            PubSub.publish('show-basic-notification', {
                title: 'Error',
                message: 'Failed to test app password. Please try again.'
            });
        } finally {
            setIsAppPasswordLoading(false);
        }
    };

    const saveAppPassword = async () => {
        if (!appPasswordForm.appPassword.trim()) {
            setAppPasswordTouched({ appPassword: true });
            return;
        }

        const error = validateAppPassword(appPasswordForm.appPassword);
        if (error) {
            setAppPasswordErrors({ appPassword: error });
            setAppPasswordTouched({ appPassword: true });
            return;
        }

        setIsAppPasswordLoading(true);
        try {
            const cleanAppPassword = appPasswordForm.appPassword.replace(/\s/g, '');
            const response = await AppPasswordService.saveAppPassword({
                account: user.email,
                password: cleanAppPassword
            });

            if (response.success) {
                PubSub.publish('show-basic-notification', {
                    title: 'Success',
                    message: 'App password saved successfully!'
                });
                setAppPasswordForm({ appPassword: '' });
                getUserAppPasswordStatus(); // Refresh status
            } else {
                PubSub.publish('show-basic-notification', {
                    title: 'Error',
                    message: response.message || 'Failed to save app password'
                });
            }
        } catch (error) {
            PubSub.publish('show-basic-notification', {
                title: 'Error',
                message: 'Failed to save app password. Please try again.'
            });
        } finally {
            setIsAppPasswordLoading(false);
        }
    };

    const removeAppPassword = async () => {
        if (!window.confirm('Are you sure you want to remove the app password for this user?')) {
            return;
        }

        setIsAppPasswordLoading(true);
        try {
            const response = await AppPasswordService.removeUserAppPassword(id);

            if (response.success) {
                PubSub.publish('show-basic-notification', {
                    title: 'Success',
                    message: 'App password removed successfully!'
                });
                getUserAppPasswordStatus(); // Refresh status
            } else {
                PubSub.publish('show-basic-notification', {
                    title: 'Error',
                    message: response.message || 'Failed to remove app password'
                });
            }
        } catch (error) {
            PubSub.publish('show-basic-notification', {
                title: 'Error',
                message: 'Failed to remove app password. Please try again.'
            });
        } finally {
            setIsAppPasswordLoading(false);
        }
    };

    return (
        <div className="container">

            <nav className="divider-breadcrumb mt-3 fs-13" aria-label="breadcrumb">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to="/admin/users">Users</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">{user.name}</li>
                </ol>
            </nav>

            <div className="row mt-5" >

                <div className="col-6">
                    <AdminProfile />

                </div>

                <div className="col-6">
                    <AdminUserSettings />

                    <div className="card mt-4">
                        <div className="card-header">
                            Role
                        </div>
                        <div className="card-body">
                            <select
                                value={user.role}
                                onChange={(e) => setUser({ ...user, role: e.target.value })}
                                className="form-select form-select-sm">
                                <option value="user">User</option>
                                <option value="whitelist">Whitelist</option>
                                <option value="admin">Admin</option>
                            </select>
                            <button
                                onClick={updateUserRole}
                                className="btn btn-primary btn-sm mt-2">
                                Save role
                            </button>
                        </div>
                    </div>

                    <div className="card mt-4">
                        <div className="card-header">
                            App Password Configuration
                        </div>
                        <div className="card-body">
                            {/* Estado actual */}
                            <div className="mb-3">
                                <strong>Status:</strong> 
                                <span className={`badge ms-2 ${appPasswordStatus.hasAppPassword ? 'bg-success' : 'bg-warning'}`}>
                                    {appPasswordStatus.hasAppPassword ? 'Connected' : 'Not Connected'}
                                </span>
                                {appPasswordStatus.account && (
                                    <div className="text-muted small mt-1">
                                        Account: {appPasswordStatus.account}
                                    </div>
                                )}
                            </div>
                            
                            {/* Formulario de app password */}
                            <div className="mb-3">
                                <label className="form-label">App Password</label>
                                <input 
                                    type="text" 
                                    className={`form-control ${appPasswordTouched.appPassword && appPasswordErrors.appPassword ? 'is-invalid' : ''}`}
                                    placeholder="Enter 16-character app password"
                                    value={appPasswordForm.appPassword}
                                    onChange={handleAppPasswordChange}
                                    onBlur={handleAppPasswordBlur}
                                    disabled={isAppPasswordLoading}
                                />
                                {appPasswordTouched.appPassword && appPasswordErrors.appPassword && (
                                    <div className="invalid-feedback">
                                        {appPasswordErrors.appPassword}
                                    </div>
                                )}
                            </div>
                            
                            {/* Botones de acción */}
                            <div className="d-flex gap-2">
                                <button 
                                    onClick={testAppPassword} 
                                    className="btn btn-outline-primary btn-sm"
                                    disabled={isAppPasswordLoading || !appPasswordForm.appPassword.trim()}>
                                    {isAppPasswordLoading ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin me-1"></i>
                                            Testing...
                                        </>
                                    ) : (
                                        'Test Connection'
                                    )}
                                </button>
                                <button 
                                    onClick={saveAppPassword} 
                                    className="btn btn-primary btn-sm"
                                    disabled={isAppPasswordLoading || !appPasswordForm.appPassword.trim()}>
                                    {isAppPasswordLoading ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin me-1"></i>
                                            Saving...
                                        </>
                                    ) : (
                                        'Save Password'
                                    )}
                                </button>
                                {appPasswordStatus.hasAppPassword && (
                                    <button 
                                        onClick={removeAppPassword} 
                                        className="btn btn-outline-danger btn-sm"
                                        disabled={isAppPasswordLoading}>
                                        {isAppPasswordLoading ? (
                                            <>
                                                <i className="fas fa-spinner fa-spin me-1"></i>
                                                Removing...
                                            </>
                                        ) : (
                                            'Remove Password'
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {campaigns.length > 0 && (
                        <div className="card mt-4">
                            <div className="card-header">
                                Campaigns
                            </div>
                            <div className="card-body p-0">
                                <ul className="list-group list-group-flush">
                                    {campaigns.map(c => (
                                        <li key={c._id} className="list-group-item d-flex justify-content-between align-items-center">
                                            {c.name}
                                            <Link className="btn btn-link btn-sm" to={`/sequences/${c._id}`}>View campaign detail</Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>

            </div>


        </div>
    )

}

export default UserDetail;
